import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Checkbox,
  Chip,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useMemo } from 'react';
import { baseFormInlineClassNames } from '../../../theme/component-styles';
import { IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { MoreChipWithTooltip } from '../../../pages/CompanyProfiles/Summary/components/MoreChipWithTooltip/MoreChipWithTooltip';
import { SelectType, useFieldSelect } from './FieldSelect';
import { IBaseFieldProps } from './types';
import { addOptionLabel, useFreeSoloEntry } from './useFreeSoloEntry';

// Rounds saved before typed multi-select storage hold comma-joined strings in customData;
// MUI's Autocomplete requires an array, so a raw string would crash the whole form.
function normalizeMultiSelectValue(raw: unknown): SelectType[] {
  if (Array.isArray(raw)) {
    return raw as SelectType[];
  }
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return raw == null ? [] : [raw as SelectType];
}

export function FieldMultiSelect(props: IBaseFieldProps<IFormFieldSelectMeta<SelectType>>) {
  const { formField, formProps } = props;
  const { ref, onChange, onBlur, value: rawValue } = formProps;
  const value = useMemo(() => normalizeMultiSelectValue(rawValue), [rawValue]);
  const { disabled = false, label, variant, placeholder } = formField ?? {};
  const { allowCustomAdd, endAdornment, inlinePopper, onCustomAdd } = formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? `${baseFormInlineClassNames} form-inline-multiselect` : '';

  const effectiveFormField = useMemo(() => {
    if (
      allowCustomAdd &&
      !formField.rendererMeta?.values?.length &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      return {
        ...formField,
        rendererMeta: {
          ...formField.rendererMeta,
          values: (value as unknown[]).map((v) => ({ displayName: String(v), value: String(v) })),
        },
      };
    }
    return formField;
  }, [allowCustomAdd, formField, value]);

  const { getOptionsLabel, options, valuesAsMap, getOptionDisabled } = useFieldSelect(effectiveFormField);

  const handleChange = useCallback(
    (values: SelectType[]) => {
      onChange(values);
      if (allowCustomAdd && onCustomAdd) {
        const newValues = values.filter((v) => !valuesAsMap.has(v));
        if (newValues.length) {
          newValues.forEach((v) => onCustomAdd(String(v)));
        }
      }
    },
    [allowCustomAdd, onChange, onCustomAdd, valuesAsMap]
  );

  const { commitPending, filterOptions, inputValue, isAddOption, onInputChange } =
    useFreeSoloEntry<SelectType>({
      enabled: !!allowCustomAdd,
      value,
      options,
      onChange: handleChange,
      getOptionLabel: getOptionsLabel,
    });

  const autocomplete = (
    <Autocomplete
      style={{ pointerEvents: 'auto' }} // otherwise hovering over the more chip won't show other values
      disabled={disabled}
      aria-label={label}
      className={className}
      disableCloseOnSelect
      filterOptions={filterOptions}
      getOptionLabel={getOptionsLabel}
      freeSolo={allowCustomAdd}
      inputValue={inputValue}
      multiple
      onBlur={() => {
        // Typed text left in the box on click out / tab out becomes a tag rather than being lost.
        commitPending();
        onBlur();
      }}
      onChange={(event, values) => handleChange(values)}
      onInputChange={onInputChange}
      openOnFocus
      options={options}
      // Opt-in per field: in flow the dropdown scrolls with its input rather than being left
      // floating, and the width floor stops long options clipping to a narrow input — but only the
      // caller knows whether an in-flow popper has a clipping ancestor. Default stays portaled.
      disablePortal={inlinePopper}
      slotProps={
        inlinePopper ? { popper: { style: { minWidth: '15rem' }, placement: 'bottom-start' } } : undefined
      }
      renderOption={(props, option, renderOptionState: AutocompleteRenderOptionState) => {
        if (isAddOption(option, renderOptionState)) {
          return (
            <MenuItem {...props} key={`add-${option}`}>
              <ListItemIcon>
                <AddIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary={addOptionLabel(option)} />
            </MenuItem>
          );
        }
        const { selected } = renderOptionState;
        const fallbackOption = allowCustomAdd
          ? { displayName: option, value: option, disabled: false, secondaryLabel: undefined }
          : undefined;
        const resolved = valuesAsMap.get(option) ?? fallbackOption;
        if (!resolved) {
          return null;
        }
        const { disabled, displayName, value, secondaryLabel = undefined } = resolved;

        return (
          <MenuItem {...props} key={option} disabled={disabled}>
            <Checkbox checked={selected} />
            <ListItemText primary={displayName ?? value} secondary={secondaryLabel} />
          </MenuItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          // The ghost text input shows its placeholder even when values render as chips beside it —
          // the empty-state hint has no business appearing next to a value.
          placeholder={Array.isArray(value) && value.length > 0 ? undefined : placeholder}
          ref={ref}
          onKeyDown={(event) => {
            // Prevent the backspace deletes tags behaviour.
            const currentText = (event.target as HTMLInputElement).value;
            if (currentText.length === 0 && event.key === 'Backspace') {
              event.stopPropagation();
            }
          }}
        />
      )}
      renderTags={(options) => {
        if (!options?.length) {
          return <></>;
        }

        const [first, ...others] = options.map((option) => {
          const fallbackOption = allowCustomAdd
            ? { displayName: String(option), value: option, secondaryLabel: undefined }
            : undefined;
          const item = valuesAsMap.get(option) ?? fallbackOption;

          return item ? (item.displayName ?? item.secondaryLabel ?? item.value.toString()) : '';
        });

        if (options.length === 1) {
          return <Chip size='small' label={first} />;
        }
        return (
          <>
            <Chip size='small' label={first} />
            <MoreChipWithTooltip
              title={`Other (${others.length})`}
              moreChipTotal={others.length}
              otherItems={others}
            ></MoreChipWithTooltip>
          </>
        );
      }}
      getOptionDisabled={getOptionDisabled}
      value={value}
    />
  );

  if (!endAdornment) {
    return autocomplete;
  }

  // Beside the input, not inside it: the adornment stays clear of the field's border and hover
  // controls, and its clicks cannot open the dropdown. `minWidth: 0` lets the input shrink so the
  // adornment never wraps.
  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: '0.25rem' }}>
      <div style={{ flexGrow: 1, minWidth: 0 }}>{autocomplete}</div>
      <span style={{ flexShrink: 0 }}>{endAdornment}</span>
    </div>
  );
}
